import { DateInput } from "../components/DateInput";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import AiRewriteButton from "../components/AiRewriteButton";

import { tr, lbl, LANG } from "../lib/i18n";
import { useBranches, branchName, pickedBranch } from "../lib/branches";
const OWNERSHIP = ["Company Owned", "Customer Consignment"];
const SPECS = ["GCC", "Imported", "Other"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const FUEL = ["Petrol", "Diesel", "Hybrid", "Electric"];

export default function VehicleFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const { multi, branches } = useBranches();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const canAssign = ["admin", "management"].includes(user?.role);
  useEffect(() => { if (canAssign) api.get("/leads/facets").then((r) => setStaff(r.data.staff)).catch(() => {}); }, [canAssign]);
  const [branchId, setBranchId] = useState(pickedBranch());
  useEffect(() => { if (multi && !branches.some((b) => b.id === branchId) && branches[0]) setBranchId(branches[0].id); }, [multi, branches, branchId]);
  const [form, setForm] = useState({
    make: "",
    model: "",
    model_year: new Date().getFullYear(),
    vin: "",
    plate_number: "",
    mileage: 0,
    exterior_color: "",
    interior_color: "",
    body_type: "",
    transmission: "Automatic",
    fuel_type: "Petrol",
    engine: "",
    cylinders: 4,
    specification: "GCC",
    ownership_type: "Company Owned",
    purchase_date: "",
    purchase_price: "",
    asking_price: "",
    minimum_selling_price: "",
    final_selling_price: "",
    description: "",
    internal_notes: "",
    publish_to_website: false,
    public_price: "",
    supplier: "",
    owner_name: "",
    status: "Available",
  });
  const [busy, setBusy] = useState(false);
  const [bodyTypes, setBodyTypes] = useState([]);
  const bodyOptions = (() => {
    const cur = form.body_type || "";
    const hit = bodyTypes.find((b) => b.toLowerCase() === cur.toLowerCase());
    return ["", ...bodyTypes, ...(cur && !hit ? [cur] : [])];
  })();

  useEffect(() => {
    api.get("/vehicles/body-types").then((r) => setBodyTypes(r.data.body_types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}`);
        setForm({ ...form, ...data });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["purchase_price", "asking_price", "minimum_selling_price", "final_selling_price", "public_price", "mileage", "cylinders", "model_year"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else if (typeof payload[k] === "string") payload[k] = Number(payload[k]);
      });
      if (isEdit) {
        await api.patch(`/vehicles/${id}`, payload);
        toast.success(tr("x.vehicle_updated"));
      } else {
        const { data } = await api.post("/vehicles", { ...payload, ...(multi && branchId ? { branch_id: branchId } : {}) });
        toast.success(tr("x.stock_created", { p0: data.stock_number }));
        nav(`/inventory/${data.id}`);
        return;
      }
      nav(`/inventory/${id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bind = (field) => ({
    value: form[field] ?? "",
    onChange: (e) => setForm({ ...form, [field]: e.target.value }),
  });

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="vehicle-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">{isEdit ? tr("x.edit_vehicle") : tr("x.new_vehicle")}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
            {isEdit ? tr("x.update_details") : tr("x.add_vehicle_to_inventory")}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => nav(-1)} className="h-10 px-4 rounded-lg border border-[hsl(var(--border))] text-sm">{tr("x.cancel")}</button>
          <button data-testid="submit-vehicle" type="submit" disabled={busy} className="h-10 px-5 rounded-lg bg-accent-brand text-white text-sm font-semibold">
            {busy ? tr("x.saving") : (isEdit ? tr("x.save_changes") : tr("x.create_vehicle"))}
          </button>
        </div>
      </div>

      <Section title={tr("x.identification")}>
        <TextField label={tr("x.make")} required {...bind("make")} testid="field-make" />
        <TextField label={tr("x.model_2")} required {...bind("model")} testid="field-model" />
        <NumberField label={tr("x.model_year")} required {...bind("model_year")} testid="field-model-year" />
        <TextField label={tr("x.vin_chassis")} {...bind("vin")} testid="field-vin" />
        <TextField label={tr("x.plate_number")} {...bind("plate_number")} testid="field-plate" />
        <SelectField label={tr("x.ownership_2")} options={OWNERSHIP} required {...bind("ownership_type")} testid="field-ownership" />
      </Section>

      <Section title={tr("x.specifications")}>
        <NumberField label={tr("x.mileage_km")} {...bind("mileage")} testid="field-mileage" />
        <TextField label={tr("x.exterior_color")} {...bind("exterior_color")} testid="field-ext-color" />
        <TextField label={tr("x.interior_color")} {...bind("interior_color")} testid="field-int-color" />
        {multi && !isEdit && (
          <label className="text-sm font-medium">{tr("br.branch")}
            <select className="mt-1 w-full h-10 border border-[var(--divider)] px-3" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="vehicle-branch-select">
              {branches.map((b) => <option key={b.id} value={b.id}>{branchName(b, LANG)}</option>)}
            </select>
          </label>
        )}
        {canAssign && (
          <label className="text-sm font-medium">{tr("ld.assigned_sp")}
            <select className="mt-1 w-full h-10 border border-[var(--divider)] px-3" value={form.assigned_salesperson_id || ""} onChange={(e) => setForm({ ...form, assigned_salesperson_id: e.target.value || null })} data-testid="vehicle-salesperson-select">
              <option value="">{tr("ld.unassigned")}</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </label>
        )}
        <BodyTypeField label={tr("x.body_type")} options={bodyOptions} {...bind("body_type")}
          value={bodyTypes.find((b) => b.toLowerCase() === (form.body_type || "").toLowerCase()) || form.body_type || ""} />
        <SelectField label={tr("x.transmission")} options={TRANSMISSIONS} {...bind("transmission")} />
        <SelectField label={tr("x.fuel_type")} options={FUEL} {...bind("fuel_type")} />
        <TextField label={tr("x.engine")} {...bind("engine")} />
        <NumberField label={tr("x.cylinders")} {...bind("cylinders")} />
        <SelectField label={tr("x.specification")} options={SPECS} {...bind("specification")} />
      </Section>

      <Section title={tr("x.pricing_purchase")}>
        <TextField label={tr("x.purchase_date")} type="date" {...bind("purchase_date")} />
        <NumberField label={tr("x.purchase_price")} step="0.001" {...bind("purchase_price")} testid="field-purchase-price" />
        <NumberField label={tr("x.asking_price")} step="0.001" {...bind("asking_price")} testid="field-asking-price" />
        <NumberField label={tr("x.minimum_selling_price")} step="0.001" {...bind("minimum_selling_price")} />
        <NumberField label={tr("x.final_selling_price")} step="0.001" {...bind("final_selling_price")} />
      </Section>

      <Section title={tr("x.website_visibility")}>
        <div className="col-span-full flex items-center gap-3">
          <input
            data-testid="publish-toggle"
            type="checkbox"
            id="publish"
            checked={!!form.publish_to_website}
            onChange={(e) => setForm({ ...form, publish_to_website: e.target.checked })}
            className="w-4 h-4 accent-[var(--accent)]"
          />
          <label htmlFor="publish" className="text-sm">{tr("x.publish_to_website")}</label>
        </div>
        <NumberField label={tr("x.public_price")} step="0.001" {...bind("public_price")} testid="field-public-price" />
      </Section>

      <Section title={tr("x.notes")}>
        <div className="col-span-full">
          <div className="flex items-center justify-between">
            <label className="eyebrow">{tr("x.public_description")}</label>
            <AiRewriteButton
              text={form.description}
              vehicleId={id}
              structuredFields={{
                make: form.make,
                model: form.model,
                model_year: form.model_year,
                mileage: form.mileage,
                specification: form.specification,
                exterior_color: form.exterior_color,
                interior_color: form.interior_color,
                transmission: form.transmission,
                fuel_type: form.fuel_type,
                engine: form.engine,
                body_type: form.body_type,
              }}
              onAccept={(v) => setForm({ ...form, description: v, description_ai_assisted: true })}
            />
          </div>
          <textarea {...bind("description")} rows={4} className="mt-2 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm" />
        </div>
        <div className="col-span-full">
          <label className="eyebrow">{tr("x.internal_notes_never_exposed_publicly")}</label>
          <textarea {...bind("internal_notes")} rows={2} className="mt-2 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm" />
        </div>
        {form.ownership_type === "Company Owned" ? (
          <TextField label={tr("x.supplier_internal")} {...bind("supplier")} />
        ) : (
          <TextField label={tr("x.consignment_owner_name")} {...bind("owner_name")} />
        )}
      </Section>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <div className="panel p-5">
      <div className="eyebrow mb-4">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

function TextField({ label, testid, ...props }) {
  const cls = "mt-2 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm outline-none focus:border-[var(--accent)]";
  return (
    <div>
      <label className="eyebrow">{label}</label>
      {props.type === "date" ? <DateInput data-testid={testid} {...props} className={cls} /> : <input data-testid={testid} {...props} className={cls} />}
    </div>
  );
}

function NumberField({ label, testid, ...props }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <input type="number" data-testid={testid} {...props} className="mt-2 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm outline-none focus:border-[var(--accent)]" />
    </div>
  );
}

function BodyTypeField({ label, options, ...props }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <select data-testid="field-body-type" {...props} className="mt-2 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm outline-none focus:border-[var(--accent)]">
        {options.map((o) => <option key={o || "none"} value={o}>{o ? lbl(o) : "—"}</option>)}
      </select>
    </div>
  );
}

function SelectField({ label, options, testid, ...props }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <select data-testid={testid} {...props} className="mt-2 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm outline-none focus:border-[var(--accent)]">
        {options.map((o) => <option key={o} value={o}>{lbl(o)}</option>)}
      </select>
    </div>
  );
}
